import { useEffect, useMemo, useState } from 'react';
import SheetOverlay, {
  SheetPillRow,
  SheetCheckRow,
  SheetDoneButton,
  SheetCancelButton,
  SheetArrow,
  SheetPhotoHead,
} from '../../../components/UISystem/SheetOverlay';
import { useConnectionContext, useConnectionData } from '../../../contexts/ConnectionStateContext';
import { commitHeldChoice } from '../../../services/exchange/heldChoice';
import { loadReceivedData } from '../../../services/sharing/shareSheetData';
import { getProfileUserId } from '../../../utils/identityUtils';
import { fullProfileFields, fullProfileRows } from '../../../utils/sharingFields';
import {
  customShareKind,
  customStartTicks,
  NAME_ITEM,
  receivedFrom,
  shareItemsOf,
  shareSheetButtons,
  toSharedFields,
} from '../../../services/sharing/shareSheetRules';
import { isActiveRoyalePeer } from '../../../utils/connectionName';
import { firstNameOf } from '../../../utils/contactUtils';

// The sheet rises this long after a scan lands (share-sheet rules 12; Anon §6c)
export const SHARE_RISE_MS = 420;

/**
 * The sheet's title (share-sheet rules 9; Mark's wording 2026-10-21): whenever
 * the other person is a Profile user (Simple or Full), `Connect with <first
 * name>`; only an unknown guest's first scan says plain `Share`. A repeat's
 * sheet has no title — its button names them (`Reconnect with <first name>`).
 *
 * @param {Object|null} peer - their record on this room (anonData), or the
 *   person a profile-to-profile sheet is about ({ name, senderProfileUserId })
 * @returns {string}
 */
export const shareSheetTitle = (peer) => {
  const first = firstNameOf(peer?.name);
  return isActiveRoyalePeer(peer) && first ? `Connect with ${first}` : 'Share';
};

/**
 * What a repeat's person already receives from you (rule 7): read once, as soon
 * as the sheet knows who it's about — on the code page that's the scan, ahead
 * of the rise. No special fallback (Mark, 2026-10-20): `Reconnect` shows at
 * once, and `Share more` appears only if and when this answer arrives; if it
 * never does, the sheet stays `Reconnect` alone.
 *
 * @returns {{personal, groupIds}|null} null = not known (yet, or ever)
 */
const useReceivedData = (room, repeat) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    if (!repeat || !room) return undefined;
    let live = true;
    loadReceivedData(getProfileUserId(), room).then((d) => live && setData(d));
    return () => {
      live = false;
    };
  }, [room, repeat]);
  return data;
};

/**
 * The exchange share sheet — one component for every side (share-sheet rules
 * 11): the host's code page after a scan, and People under a Profile scanner
 * (plan step 10). What it offers is shareSheetRules' (the ruled matrix): at
 * most three at rest — `Full profile` (black), groups or your last Custom set,
 * `Custom` (white) — and the arrow (`More to share`) for the rest. A tap on a
 * set shares it and ends the sheet; `Custom` opens its list, which ends in its
 * own black `Share`. A tap outside folds it: nothing more shared — the
 * connection already formed at the scan (rule 13).
 *
 * Every share rides the one commit path (commitSharedFields) and records its
 * choice server side (record_share, plan step 4).
 *
 * **A repeat connect (rule 7, plan step 13; Mark's spec 2026-10-20):** one
 * black `Reconnect` — the `Full profile` pill, relabelled — which shares
 * nothing new and ends the sheet; under it the small `Share more` link (the
 * sheet family's second-choice text button, SheetCancelButton) only when
 * something is left to share. `Share more` swaps in the first-connect sheet,
 * whose Custom starts with what this person already receives ticked.
 *
 * @param {Object} p
 * @param {boolean} p.open
 * @param {string|null} p.room - who it's about
 * @param {Object|null} p.profile - your own profile
 * @param {{groups: Array, lastCustom: string[]|null}} [p.sheetData] - shareSheetData
 * @param {(kind: string) => void} p.onShared - a share went out
 * @param {Function} p.onFold - a tap outside
 * @param {boolean} [p.repeat=false] - a repeat connect (reconnect or re-meet)
 * @param {{profileUserId, name, photoUrl, photoMeta?, colour?}} [p.peer] - two
 *   profile holders (E3b-2a): who the sheet is about, before anything is
 *   recorded — the title and the photo head come from them (PXP §3b H5)
 * @param {(choice: Object) => void} [p.onChosen] - two profile holders: a tap
 *   HOLDS the choice instead of sharing (Mark, 2026-10-21: nothing is recorded
 *   until both have tapped); the page commits it once the server says formed
 *   (commitHeldChoice). `room` is then the room you already share on a repeat
 *   (what they receive), or null.
 */
const ExchangeShareSheet = ({
  open,
  room,
  profile,
  sheetData,
  onShared,
  onFold,
  repeat = false,
  peer = null,
  onChosen = null,
}) => {
  const [view, setView] = useState('buttons'); // 'buttons' | 'custom'
  const [more, setMore] = useState(false);
  // A repeat's `Share more` tapped: the first-connect sheet from here on
  const [shareMore, setShareMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const connectionData = useConnectionData(room);
  const { updateContactSharingStatus } = useConnectionContext();
  const who = peer
    ? { ...peer, senderProfileUserId: peer.profileUserId }
    : connectionData?.anonData;

  // A new person (or the sheet coming back) starts at its buttons, arrow closed
  useEffect(() => {
    if (!open) return;
    setView('buttons');
    setMore(false);
    setShareMore(false);
  }, [open, room]);

  const held = useMemo(() => shareItemsOf(profile), [profile]);
  const receivedData = useReceivedData(room, repeat);
  // What they already receive, over your groups' share sets (loadShareSheetData)
  const received = useMemo(() => {
    if (!receivedData) return null;
    const ids = new Set(receivedData.groupIds);
    return receivedFrom({
      held,
      personal: receivedData.personal,
      memberOf: (sheetData?.groups || []).filter((g) => ids.has(g.id)),
    });
  }, [receivedData, held, sheetData]);
  const asRepeat = repeat && !shareMore;
  const peerFirst = firstNameOf(who?.name) || null;
  // A repeat's sheet has no title; `Share more` brings the first-connect title
  const title = asRepeat ? null : shareSheetTitle(who);
  const head = peer ? (
    <SheetPhotoHead
      photoUrl={peer.photoUrl}
      photoMeta={peer.photoMeta}
      name={peer.name}
      colour={peer.colour}
    />
  ) : null;
  const sheet = useMemo(() => {
    if (asRepeat) return shareSheetButtons({ connect: 'repeat', profile, received, peerFirst });
    return shareSheetButtons({
      connect: 'first',
      profile,
      groups: sheetData?.groups || [],
      lastCustom: sheetData?.lastCustom || null,
    });
  }, [asRepeat, profile, received, sheetData, peerFirst]);

  const commit = async (sharedFields, isFullName, kind, shareKind) => {
    const choice = { kind, sharedFields, isFullName, shareKind };
    if (onChosen) {
      // Two profile holders: held until both have tapped
      onChosen(choice);
      return;
    }
    if (busy || !room || !profile?.name) return;
    setBusy(true);
    try {
      await commitHeldChoice({
        choice,
        room,
        profile,
        connectionData,
        updateContactSharingStatus,
      });
    } finally {
      setBusy(false);
      onShared(kind);
    }
  };

  const tap = (b) => {
    if (b.kind === 'reconnect') {
      // Shares nothing new: what already flows keeps flowing (rule 7)
      if (busy) return;
      if (onChosen) onChosen({ kind: 'reconnect' });
      else onShared('reconnect');
      return;
    }
    if (b.kind === 'share_more') {
      setShareMore(true);
      return;
    }
    if (b.kind === 'custom') {
      setView('custom');
      return;
    }
    if (b.kind === 'full') {
      commit(fullProfileFields(profile), true, 'full', { kind: 'full' });
      return;
    }
    const { sharedFields, isFullName } = toSharedFields(b.items);
    if (b.kind === 'group') {
      commit(sharedFields, isFullName, 'group', { kind: 'group', groupId: b.groupId });
    } else {
      commit(
        sharedFields,
        isFullName,
        'last_custom',
        customShareKind(held, b.items, { kind: 'last_custom' })
      );
    }
  };

  const pill = (b) =>
    b.kind === 'share_more' ? (
      // The small link under Reconnect: the sheet family's second-choice text
      // button (SheetCancelButton — Cancel under Done elsewhere)
      <SheetCancelButton key="share_more" label={b.label} onClick={() => tap(b)} />
    ) : (
      <SheetPillRow
        key={`${b.kind}:${b.groupId || b.label}`}
        label={b.label}
        selected={b.kind === 'full' || b.kind === 'reconnect'}
        onClick={() => tap(b)}
      />
    );

  return (
    <>
      <SheetOverlay
        open={open && view === 'buttons'}
        soft
        bigTitle
        centred
        title={title}
        head={head}
        onClose={onFold}
      >
        {sheet.atRest.map(pill)}
        {more ? sheet.behindArrow.map(pill) : null}
        {/* The arrow (rule 5): under the last button; once opened it's gone */}
        {!more && sheet.behindArrow.length > 0 ? (
          <SheetArrow label="More to share" onClick={() => setMore(true)} />
        ) : null}
      </SheetOverlay>

      {/* Custom keeps the sheet's title (rule 6: "title stays") */}
      <SheetOverlay
        open={open && view === 'custom'}
        soft
        bigTitle
        centred
        title={title || shareSheetTitle(who)}
        head={head}
        onClose={onFold}
      >
        <CustomList
          profile={profile}
          held={held}
          start={customStartTicks(shareMore ? 'share_more' : 'first', held, received)}
          busy={busy}
          onShare={(ticked) => {
            const { sharedFields, isFullName } = toSharedFields(ticked);
            commit(sharedFields, isFullName, 'custom', customShareKind(held, ticked));
          }}
        />
      </SheetOverlay>
    </>
  );
};

/**
 * Custom (share-sheet rules 6): your name first, no icon (ticked = full name,
 * unticked = first name) — no row for a one-word name, the switch would do
 * nothing — then Lives in, About me and each of your details (DETAIL §8e's
 * order, CD Drop 17), each untickable, then the black `Share`. What starts
 * ticked (Mark, 2026-10-19): on a first connect your full name and Lives in
 * only, you tick the rest; through a repeat's `Share more`, what this person
 * already receives (customStartTicks). `Share` works with nothing ticked and is never
 * greyed out (only while a share is in flight).
 */
const CustomList = ({ profile, held, start, busy, onShare }) => {
  // Labels and icons for the detail rows (one home: sharingFields)
  const display = useMemo(
    () => Object.fromEntries(fullProfileRows(profile).map((r) => [r.key, r])),
    [profile]
  );
  // What starts ticked (rule 6): read once, as the list opens
  const [on, setOn] = useState(() => start);
  const toggle = (k) =>
    setOn((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });

  return (
    <>
      {held.map((h) => {
        const row =
          h.itemKey === NAME_ITEM
            ? { label: profile?.name || '' }
            : display[h.field] || { label: h.field };
        return (
          <SheetCheckRow
            key={h.itemKey}
            label={row.label}
            icon={row.icon}
            indent={!row.icon}
            on={on.has(h.itemKey)}
            onToggle={() => toggle(h.itemKey)}
          />
        );
      })}
      <SheetDoneButton
        label="Share"
        disabled={busy}
        onClick={() => onShare(held.map((h) => h.itemKey).filter((k) => on.has(k)))}
      />
    </>
  );
};

export default ExchangeShareSheet;
